import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Input, 
  Button, 
  Space, 
  Typography,
  Row,
  Col,
  Select,
  DatePicker,
  Tag,
  Statistic
} from 'antd';
import type { TablePaginationConfig } from 'antd/es/table';
import { 
  SearchOutlined,
  DownloadOutlined,
  EyeOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom'; 
import AdminLayout from '../../../components/Layouts/AdminLayout/AdminLayout';
import dayjs from 'dayjs';
import { adminPaperSalesApi, adminPaperApi } from '@/services/api';
import { formatNumber } from '@/utils/number';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface SalesData {
  id: string;
  no: number;
  academyCode: string;
  academyName: string;
  purchasedTickets: number;
  totalPayment: number;
  usedTickets: number;
  remainingTickets: number;
  refundedTickets: number;
  purchaseDate: string;
  status: string;
  paperTitle?: string;
}

const AdminPaperSales: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { paperId: routePaperId } = useParams();
  const [paperTitle, setPaperTitle] = useState<string>('');
  const [salesData, setSalesData] = useState<SalesData[]>([]);
  const [filteredData, setFilteredData] = useState<SalesData[]>([]);
  const [searchText, setSearchText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [dateRange, setDateRange] = useState<any>(null);

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
  });

  // 서버 데이터 로드
  const fetchSales = async (page = pagination.current || 1, pageSize = pagination.pageSize || 10) => {
    try {
      setLoading(true);
      const paperId = routePaperId ? Number(routePaperId) : undefined;
      const res = await adminPaperSalesApi.list({ paperId, page: (page - 1), size: pageSize });
      const payload = res?.data ?? res;
      const pageData = payload?.data?.content ? payload.data : payload?.data?.data?.content ? payload.data.data : payload;
      const content = Array.isArray(pageData?.content) ? pageData.content : [];
      const totalElements = pageData?.totalElements ?? pageData?.total ?? payload?.data?.totalElements ?? content.length;

      if (Array.isArray(content)) {
        const baseIndex = ((page - 1) * pageSize);
        const rows = content.map((it: any, idx: number) => ({
          id: String(it.id),
          no: totalElements > 0 ? totalElements - (baseIndex + idx) : content.length - idx,
          academyCode: it.academyCode,
          academyName: it.academyName,
          purchasedTickets: Number(it.purchasedTickets || 0),
          totalPayment: Number(it.totalPayment || 0),
          usedTickets: Number(it.usedTickets || 0),
          remainingTickets: Number(it.remainingTickets || 0),
          refundedTickets: Number(
            it.refundedTickets ??
            it.refundedCount ??
            it.refundCompletedTickets ??
            it.completedRefundTickets ??
            0
          ),
          purchaseDate: it.purchaseDate ? String(it.purchaseDate).slice(0, 10) : '',
          status: String(it.status || 'active').toLowerCase(),
          paperTitle: it.paperTitle,
        }));
        setSalesData(rows);
        setFilteredData(rows);
        if (paperId && !paperTitle && rows.length > 0 && rows[0].paperTitle) {
          setPaperTitle(rows[0].paperTitle);
        }
        setPagination((prev) => ({
          ...prev,
          current: page,
          pageSize,
          total: totalElements,
        }));
      } else {
        setSalesData([]);
        setFilteredData([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
      }
    } catch (e) {
      console.error('판매내역 조회 실패', e);
      setSalesData([]);
      setFilteredData([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSales(1, pagination.pageSize || 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routePaperId]);

  // 시험명 로드 (판매내역이 비어있더라도 표시 가능)
  useEffect(() => {
    const loadTitle = async () => {
      const pid = routePaperId ? Number(routePaperId) : undefined;
      if (!pid) return;
      try {
        const res = await adminPaperApi.detail(pid);
        const t = res?.data?.title || res?.title || res?.data?.name || res?.name;
        if (t) setPaperTitle(String(t));
      } catch {}
    };
    loadTitle();
  }, [routePaperId]);

  useEffect(() => {
    let filtered = [...salesData];

    // 검색 필터
    if (searchText) {
      filtered = filtered.filter(item =>
        item.academyCode.toLowerCase().includes(searchText.toLowerCase()) ||
        item.academyName.toLowerCase().includes(searchText.toLowerCase())
      );
    }



    // 날짜 범위 필터
    if (dateRange && dateRange[0] && dateRange[1]) {
      filtered = filtered.filter(item => {
        const purchaseDate = dayjs(item.purchaseDate);
        return purchaseDate.isAfter(dateRange[0].subtract(1, 'day')) && 
               purchaseDate.isBefore(dateRange[1].add(1, 'day'));
      });
    }

    setFilteredData(filtered);
  }, [salesData, searchText, selectedStatus, dateRange]);

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
  };

  const handleDateRangeChange = (dates: any) => {
    setDateRange(dates);
  };

  const handleReset = () => {
    setSearchText('');
    setSelectedStatus('');
    setDateRange(null);
    fetchSales(1, pagination.pageSize || 10);
  };

  const handleExport = () => {
    // 엑셀 내보내기 로직
    console.log('Export data:', filteredData);
  };

  const handleViewDetail = (record: SalesData) => {
    // 상세 페이지로 이동
    navigate(`/admin/paper/sales/detail/${record.id}`);
  };

  const statusTagMap: Record<string, { color: string; label: string }> = {
    active: { color: 'green', label: '활성' },
    completed: { color: 'blue', label: '완료' },
    refunded: { color: 'red', label: '환불' },
    paid: { color: 'cyan', label: '결제완료' },
    pending: { color: 'orange', label: '대기' },
    cancelled: { color: 'default', label: '취소' },
    canceled: { color: 'default', label: '취소' },
  };

  const getStatusTag = (status: string) => {
    const key = (status || '').toLowerCase();
    const meta = statusTagMap[key];
    if (meta) {
      return <Tag color={meta.color}>{meta.label}</Tag>;
    }
    return <Tag color="default">{status || '-'}</Tag>;
  };

  const columns = [
    {
      title: '순번',
      dataIndex: 'no',
      key: 'no',
      width: 80,
      align: 'center' as const,
      render: (no: number) => formatNumber(no),
    },
    {
      title: '학원코드',
      dataIndex: 'academyCode',
      key: 'academyCode',
      width: 120,
    },
    {
      title: '학원명',
      dataIndex: 'academyName',
      key: 'academyName',
      width: 150
    },
    {
      title: '구매한 응시권 수',
      dataIndex: 'purchasedTickets',
      key: 'purchasedTickets',
      width: 140,
      align: 'center' as const,
      render: (value: number) => formatNumber(value),
    },
    {
      title: '총 결제금액',
      dataIndex: 'totalPayment',
      key: 'totalPayment',
      width: 130,
      align: 'right' as const,
      render: (value: number) => `${formatNumber(value)}원`
    },
    {
      title: '소진한 응시권 수',
      dataIndex: 'usedTickets',
      key: 'usedTickets',
      width: 140,
      align: 'center' as const,
      render: (value: number) => formatNumber(value),
    },
    {
      title: '잔여 응시권 수',
      dataIndex: 'remainingTickets',
      key: 'remainingTickets',
      width: 140,
      align: 'center' as const,
      render: (value: number) => formatNumber(value),
    },
    {
      title: '환불 완료 응시권 수',
      dataIndex: 'refundedTickets',
      key: 'refundedTickets',
      width: 160,
      align: 'center' as const,
      render: (value: number) => formatNumber(value),
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center' as const,
      render: (status: string) => getStatusTag(status)
    },
  ];

  // 통계 계산
  const totalPurchased = filteredData.reduce((sum, item) => sum + item.purchasedTickets, 0);
  const totalPayment = filteredData.reduce((sum, item) => sum + item.totalPayment, 0);
  const totalUsed = filteredData.reduce((sum, item) => sum + item.usedTickets, 0);
  const totalRemaining = filteredData.reduce((sum, item) => sum + item.remainingTickets, 0);
  const totalRefunded = filteredData.reduce((sum, item) => sum + item.refundedTickets, 0);

  const handleTableChange = (pager: TablePaginationConfig) => {
    const current = pager.current || 1;
    const size = pager.pageSize || pagination.pageSize || 10;
    fetchSales(current, size);
  };

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <Space direction="vertical" size={8}>
            <Button onClick={() => navigate('/admin/paper')}>목록으로</Button>
            <Title level={2} style={{ margin: 0 }}>{paperTitle ? `${paperTitle} 판매내역` : '시험 판매 내역'}</Title>
          </Space>
        </div>

        {/* 검색 및 필터 */}
        
          <Row gutter={16} align="middle" className='mb-4'>
            <Col span={6}>
              <Input
                placeholder="학원코드 또는 학원명 검색"
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                allowClear
                size="large"
              />
            </Col>
            <Col span={6}>
              <Button onClick={handleReset} size="large" icon={<ReloadOutlined />}>초기화</Button>
            </Col>
          </Row>
        

        {/* 데이터 테이블 */}
        <Card>
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey="id"
            loading={loading}
            locale={{ emptyText: '판매내역 없습니다' }}
            pagination={{
              ...pagination,
              showTotal: (total, range) => {
                if (!total) return `0-0 / 총 ${formatNumber(0)}건`;
                const start = Math.max(total - range[1] + 1, 1);
                const end = Math.max(total - range[0] + 1, 1);
                return `${formatNumber(start)}-${formatNumber(end)} / 총 ${formatNumber(total)}건`;
              }
            }}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
          />
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminPaperSales; 